// Card positions for a diagram: positions saved in the browser first, automatic layout for the rest.
// Each arrangement (left to right, grid, by namespace, ...) keeps its own positions.
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../lib/db';
import { autoLayout } from './layout';
import { layoutStorageKey, type LayoutEdge, type LayoutKind, type LayoutNode, type Positions, type Spacing } from './layouts';
import { useDiagramSettings } from './settings';

export interface LayoutInput {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
}

/** Places nodes that have no saved position to the right of the saved ones. */
function placeMissing(saved: Positions, auto: Positions, missing: LayoutNode[]): Positions {
  const out = { ...saved };
  const savedValues = Object.values(saved);
  const maxX = Math.max(...savedValues.map((p) => p.x)) + 420;
  const minY = Math.min(...savedValues.map((p) => p.y));
  const autoMissing = missing.map((n) => auto[n.id]!).filter(Boolean);
  const offsetX = maxX - Math.min(...autoMissing.map((p) => p.x));
  const offsetY = minY - Math.min(...autoMissing.map((p) => p.y));
  for (const n of missing) {
    const p = auto[n.id];
    if (p) out[n.id] = { x: p.x + offsetX, y: p.y + offsetY };
  }
  return out;
}

async function seedPositions(keys: (string | null)[]): Promise<Positions> {
  const out: Positions = {};
  for (const key of keys) {
    if (!key) continue;
    const positions = (await db.layouts.get(key))?.positions ?? {};
    for (const [id, p] of Object.entries(positions)) out[id] ??= p;
  }
  return out;
}

/**
 * Positions for `input`, saved under `layoutKey` and the chosen arrangement. Returns null until the
 * first layout is ready; later inputs keep the positions they already have and place new cards
 * beside them. Switching the arrangement keeps the old positions on screen until the new ones are
 * ready (`pending` meanwhile).
 */
export function useLayout(layoutKey: string | null, input: LayoutInput | null, seedKeys?: (string | null)[]) {
  const kind: LayoutKind = useDiagramSettings((s) => s.layout);
  const spacing: Spacing = useDiagramSettings((s) => s.spacing);
  const key = layoutKey ? layoutStorageKey(layoutKey, kind, spacing) : null;
  const [positions, setPositions] = useState<Positions | null>(null);
  const [pending, setPending] = useState(false);
  // The positions in use, updated at once (state updates are applied later), so a card placed by
  // `save` just before the input changes is not treated as missing.
  const latest = useRef<{ key: string; positions: Positions } | null>(null);

  const commit = useCallback((key: string, next: Positions, persist: boolean) => {
    latest.current = { key, positions: next };
    setPositions(next);
    if (persist) void db.layouts.put({ key, positions: next, updated: Date.now() });
  }, []);

  useEffect(() => {
    if (!input || !key) return;
    let cancelled = false;
    void (async () => {
      const known = latest.current?.key === key ? latest.current.positions : undefined;
      const stored = (await db.layouts.get(key))?.positions;
      // A new layout starts from others (a comparison from its models' diagrams; the first wins).
      let saved = known ?? stored ?? (await seedPositions((seedKeys ?? []).map((k) => (k ? layoutStorageKey(k, kind, spacing) : null))));
      // Positions stored meanwhile by someone else (a renamed card keeps its place).
      if (known && stored) {
        const extra = Object.fromEntries(input.nodes.filter((n) => !known[n.id] && stored[n.id]).map((n) => [n.id, stored[n.id]!]));
        if (Object.keys(extra).length) saved = { ...known, ...extra };
      }
      const missing = input.nodes.filter((n) => !saved[n.id]);
      if (!missing.length) {
        if (!cancelled) {
          commit(key, saved, saved !== known && saved !== stored);
          setPending(false);
        }
        return;
      }
      if (!cancelled) setPending(true);
      const auto = await autoLayout(input.nodes, input.edges, kind, spacing);
      if (cancelled) return;
      setPending(false);
      const current = latest.current?.key === key ? latest.current.positions : saved;
      const stillMissing = input.nodes.filter((n) => !current[n.id]);
      // Kept, so cards that appear later (a new migration, a live edit) do not move the others.
      commit(key, stillMissing.length === input.nodes.length ? auto : placeMissing(current, auto, stillMissing), true);
    })();
    return () => {
      cancelled = true;
    };
    // `seedKeys` only matters for a layout that does not exist yet, and is a new array each render.
  }, [input, key, kind, spacing, commit]);

  const save = useCallback(
    (update: Positions) => {
      if (!key) return;
      const current = latest.current?.key === key ? latest.current.positions : {};
      commit(key, { ...current, ...update }, true);
    },
    [key, commit],
  );

  const relayout = useCallback(async () => {
    if (!input || !key) return;
    setPending(true);
    commit(key, await autoLayout(input.nodes, input.edges, kind, spacing), true);
    setPending(false);
  }, [input, key, kind, spacing, commit]);

  return { positions, save, relayout, pending };
}

/**
 * Layout input for a graph: parents (principals, base types) before dependents and derived types;
 * on a code map, callers before what they call.
 */
export function layoutInput(graph: {
  nodes: { id: string; width: number; height: number; namespace?: string | null; schema?: string | null }[];
  edges: { id: string; source: string; target: string; kind?: string }[];
}): LayoutInput {
  return {
    nodes: graph.nodes.map((n) => ({ id: n.id, width: n.width, height: n.height, namespace: n.namespace ?? null, schema: n.schema ?? null })),
    edges: graph.edges.map((e) => (e.kind === 'call' || e.kind === 'uses' ? { id: e.id, from: e.source, to: e.target } : { id: e.id, from: e.target, to: e.source })),
  };
}
